import { Button } from "@/components/atoms/buttons";
import RadioShipping from "@/components/atoms/radio-shipping";
import ModalTemplate from "@/components/molecules/modal-template";
import EmptyAddressCard from "@/components/organisms/empty-address-card";
import ShippingAddressForm from "@/components/organisms/forms/checkout/shipping-address-form";
import { FormField, FormItem, FormMessage } from "@/components/ui/form";
import { Locales } from "@/lib/i18n";
import { __ } from "@/lib/i18n/functions";
import { getAddressMetadata, getDifferentAddress } from "@/lib/utils";
import { DeliveryAndBillingSchema } from "@/lib/validation/payment";
import { useEffect, useState, useTransition } from "react";
import { useFormContext } from "react-hook-form";
import { StoreCustomerAddress } from "@medusajs/types";
import { sdk } from "@/lib/medusa/client";
import { toast, Toast } from "@/components/atoms/toast";
import { useCustomer } from "@/hooks/useCustomer";
import ShippingAddressCard from "@/components/templates/Checkout/components/shipping-address-card";
import { Loader2 } from "lucide-react";
import { useCheckoutStore } from "@/stores/checkout-store";
import { getQueryClient } from "@/lib/query";
import { companyAddressesQueryKey, useAllEmployees, useCompanyAddresses, useCompanyDetails } from "@/queries/company";
import { DeliveryAddress } from "@/types";

type ShippingDetailsProps = {
  locale: Locales;
  customerAddresses: StoreCustomerAddress[] | undefined;
  accountTypeValue: "individual" | "company";
};

const ShippingDetails = ({ locale, customerAddresses, accountTypeValue }: ShippingDetailsProps) => {
  const queryClient = getQueryClient();
  const {
    control,
    setValue,
    formState: { errors },
    clearErrors,
    getValues,
  } = useFormContext<DeliveryAndBillingSchema>();
  const { customer, refreshCustomer } = useCustomer();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPending, startTransition] = useTransition();
  const [deleteModalOpenId, setDeleteModalOpenId] = useState<string | null>(null);
  const [addressToEdit, setAddressToEdit] = useState<DeliveryAddress | null>(null);
  const setData = useCheckoutStore((state) => state.setData);
  const deliveryAddressFromStore = useCheckoutStore((state) => state.deliveryAddress);
  const { shippingAddress: customerShippingAddress } = getDifferentAddress(customerAddresses);
  const { data: companyDetails, isLoading: isCompanyLoading } = useCompanyDetails(
    customer?.company_name || "",
    customer?.id || "anonymous"
  );
  const { data: employees } = useAllEmployees(companyDetails?.id || "");
  const { data: companyAddresses, isLoading: isCompanyAddressesLoading } = useCompanyAddresses(
    accountTypeValue === "company" ? companyDetails?.id : ""
  );
  const { shippingAddress: companyShippingAddress } = getDifferentAddress(companyAddresses);
  const isCompanyAccountSelected = accountTypeValue === "company";
  const isCompanyAddressMode = isCompanyAccountSelected && !!companyDetails?.id;
  const shippingAddress = isCompanyAccountSelected ? companyShippingAddress : customerShippingAddress;
  const currentEmployee = employees
    ?.filter((employee) => employee.customer)
    .find((employee) => employee.customer.id === customer?.id);
  const canManageCompanyAddresses =
    !isCompanyAccountSelected || customer?.id === companyDetails?.created_by || currentEmployee?.is_admin;
  const addressSource = isCompanyAccountSelected ? "company" : "customer";

  useEffect(() => {
    if (shippingAddress && shippingAddress[0] && shippingAddress.length === 1) {
      const currentValue = getValues("deliveryAddress");
      const addressId = shippingAddress[0].id;
      if (currentValue !== addressId) {
        setValue("deliveryAddress", addressId);
        clearErrors("deliveryAddress");
      }
    }
  }, [shippingAddress, setValue, clearErrors, getValues]);

  const handleAddNewAddress = () => {
    setAddressToEdit(null);
    setIsModalOpen(true);
  };

  const handleEditAddress = (address: DeliveryAddress) => {
    setAddressToEdit(address);
    setIsModalOpen(true);
  };

  const handleDeleteAddress = (addressId: string) => {
    startTransition(async () => {
      try {
        if (isCompanyAddressMode && companyDetails?.id) {
          await sdk.client.fetch(`store/companies/${companyDetails.id}/addresses/${addressId}`, {
            method: "DELETE",
          });
          queryClient.invalidateQueries({ queryKey: companyAddressesQueryKey(companyDetails.id) });
        } else {
          await sdk.store.customer.deleteAddress(addressId);
          await refreshCustomer();
        }
        if (deliveryAddressFromStore && addressId === deliveryAddressFromStore) {
          setData({ deliveryAddress: "" });
          setValue("deliveryAddress", "");
          clearErrors("deliveryAddress");
        }
        toast.success({ title: __("Address deleted successfully", locale) });
        setDeleteModalOpenId(null);
      } catch (error) {
        toast.error({
          title: __("Error deleting address", locale),
          description: __("Please try again later.", locale),
        });
        console.error("Error deleting address:", error);
      }
    });
  };

  if (isCompanyAccountSelected && !isCompanyLoading && !companyDetails?.id) {
    return (
      <Toast
        id="company-addresses-missing-company"
        type="info"
        title={__("Company not found", locale)}
        className="max-w-none shadow-none"
      />
    );
  }

  if (shippingAddress === undefined || (isCompanyAccountSelected && (isCompanyLoading || isCompanyAddressesLoading))) {
    return (
      <div className="flex h-40 items-center justify-center">
        <Loader2 className="animate-spin" />
      </div>
    );
  }

  if (shippingAddress.length === 0 || !shippingAddress[0]) {
    return (
      <>
        {canManageCompanyAddresses ? (
          <EmptyAddressCard locale={locale} modalTitle={__("Add a shipping address", locale)}>
            <ShippingAddressForm
              locale={locale}
              onSuccess={() => setIsModalOpen(false)}
              addressSource={addressSource}
              companyId={companyDetails?.id}
            />
          </EmptyAddressCard>
        ) : (
          <Toast
            id="company-addresses-readonly"
            type="info"
            title={__("No company delivery address is available. Ask a company admin to add one.", locale)}
            className="max-w-none shadow-none"
          />
        )}
        {errors.deliveryAddress && <p className="text-pink-primary text-sm">{errors.deliveryAddress.message}</p>}
      </>
    );
  }

  return (
    <>
      {shippingAddress && shippingAddress.length >= 1 && (
        <FormField
          control={control}
          name="deliveryAddress"
          render={({ field }) => (
            <FormItem className="w-full gap-1">
              {shippingAddress.length > 1 ? (
                <RadioShipping
                  locale={locale}
                  name="delivery-address"
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                  className="max-w-none gap-4"
                  hasBorder
                  isPending={isPending}
                  items={shippingAddress.map((address) => {
                    const metadata = getAddressMetadata(address);

                    return {
                      value: address.id,
                      content: {
                        title: address.address_name || metadata.address_name,
                        description: address.address_1!,
                        postalCode: address.postal_code || "",
                        city: address.city || "",
                      },
                      onDelete: canManageCompanyAddresses ? () => handleDeleteAddress(address.id) : undefined,
                      onEdit: canManageCompanyAddresses ? () => handleEditAddress(address) : undefined,
                      deleteModalOpen: deleteModalOpenId === address.id,
                      onDeleteModalChange: (open: boolean) => setDeleteModalOpenId(open ? address.id : null),
                    };
                  })}
                />
              ) : (
                <ShippingAddressCard
                  setValue={setValue}
                  type="checkout"
                  locale={locale}
                  address={shippingAddress[0]}
                  addressSource={addressSource}
                  companyId={companyDetails?.id}
                  canManage={canManageCompanyAddresses}
                />
              )}
              <FormMessage className="text-base font-medium" />
            </FormItem>
          )}
        />
      )}
      {canManageCompanyAddresses && (
        <ModalTemplate
          open={isModalOpen}
          onOpenChange={setIsModalOpen}
          classNameTitle="font-cabinet-grotesk leading-h1 text-lg md:text-1xl"
          title={__(addressToEdit ? "Edit shipping address" : "Add a shipping address", locale)}
          triggerAsChild
          modalTrigger={
            <Button
              variant="secondary"
              size="lg"
              className="border-primary-light/50 font-cabinet-grotesk bg-inherit text-lg font-semibold max-sm:h-12 sm:text-xl"
              onClick={handleAddNewAddress}
            >
              {__("Add an address", locale)}
            </Button>
          }
        >
          <ShippingAddressForm
            locale={locale}
            onSuccess={() => {
              setIsModalOpen(false);
              setAddressToEdit(null);
            }}
            addressToEdit={addressToEdit}
            addressId={addressToEdit?.id}
            addressSource={addressSource}
            companyId={companyDetails?.id}
          />
        </ModalTemplate>
      )}
    </>
  );
};

export default ShippingDetails;
